/**
 * workbookPaidKeys — event keys the OLD system's own workbook already paid,
 * captured once so the run never re-prices them.
 *
 * Mikko, 2026-09-25, after seeing the workbook payroll check's AT-RISK list:
 * "this workbook is to label all WIPs in BQC5 that's already paid so in our
 * system in i-synergy Subcontractors Module will be update and capture all
 * Paid WIps and not yet Paid." The check (qcPayrollRecon.ts) only ever READ
 * the gap; this is what makes the capture durable — the point being that a
 * WIP the old VBA system already paid must never be priced a second time by
 * this app's own run, and the app has to remember that on its own from now
 * on rather than re-deriving it from a workbook file every time.
 *
 * KEYED ON THE PASS, NOT THE WIP — the same reason `keyOverrides.ts` is:
 * `qcEvents.ts`'s "wb:<date>:<wip>:<qty>:<serial>" already names one bundle,
 * and a WIP inspected twice is two passes worth different money. Storing bare
 * WIP ids instead would be smaller but wrong — it would silently swallow a
 * SECOND, later pass on a WIP the workbook only paid once.
 *
 * CHUNKED, NOT ONE BLOB — because there can be tens of thousands of these.
 * `iSyn2_SubconPayrollSettings.settingText` is a Note column, and this
 * codebase already treats that as a real, enforced budget (LOGO_MAX_CHARS,
 * payslipBranding.ts): 58,000 characters with margin to spare, not "however
 * big SharePoint will let a field grow before something breaks in practice".
 * A workbook of BQC5's size (measured: 34,284 rows, 33,143 distinct paid
 * events) as one JSON array of ~38-character keys is well over a megabyte —
 * an order of magnitude past that budget — so it is split the way
 * `iSyn2_OrderStatus` already splits IG COM's order history: many small rows
 * under one key prefix, read back and unioned, rather than one row asked to
 * hold everything.
 *
 * ADDITIVE ONLY. A capture UNIONS into whatever is already stored and never
 * removes a key — the one way this could cost real money is a key vanishing
 * and the run pricing that pass again, so nothing here has a delete path.
 * Undoing a bad capture is a person's decision, made once, by hand.
 *
 * Pure and I/O-free; SubconPayrollService does the chunk reads and writes.
 */

/** Every chunk row's settingKey starts with this, followed by its index. */
export const WORKBOOK_PAID_KEYS_PREFIX = "workbookPaidKeys:";
/**
 * Where the chunk COUNT is stored. Written last on every capture, so a
 * settings list somebody browses by hand always shows how many chunk rows to
 * expect. `getWorkbookPaidKeys` itself no longer reads this — it filters
 * `settingKey` on `WORKBOOK_PAID_KEYS_PREFIX` in one indexed request instead
 * of reading this count and then one row per chunk, which was 13 round trips
 * on the real file. Kept for the write side and for a human reading the raw
 * list; nothing in the app reads it any more.
 */
export const WORKBOOK_PAID_KEYS_COUNT_KEY = "workbookPaidKeysCount";

/**
 * Keys per chunk. Measured on the real file (34,284 rows, 33,381 distinct
 * events before the payroll-stat split): keys average 32 characters, so a
 * chunk of 3,000 is ~108,000 characters as JSON — under twice
 * `LOGO_MAX_CHARS`'s own 58,000-character budget for one settings row, with
 * BQC5's whole history needing twelve chunks. Re-measure this constant if a
 * future workbook is several times the current size.
 */
export const CHUNK_SIZE = 3_000;

/** Split a key list into chunk-sized arrays, in the order given — callers
 *  that want a stable read pass a SORTED array in, this does not sort. */
export function chunkKeys(keys: readonly string[]): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < keys.length; i += CHUNK_SIZE) out.push(keys.slice(i, i + CHUNK_SIZE));
  return out.length ? out : [[]];
}

/**
 * Parse one chunk row. Never throws: a chunk that will not parse is treated
 * as empty rather than failing the whole read, the same as every other
 * settings blob in this module (parseStoredKeyOverrides, parseStoredForm).
 */
export function parseKeyChunk(text: string | null | undefined): string[] {
  if (!text) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    const k = String(v ?? "").trim();
    if (k) out.push(k);
  }
  return out;
}

/** Union new keys into what is already captured, deduplicated, for
 *  re-chunking and writing whole. Order is not meaningful; a Set collapses
 *  duplicates from the workbook (exact-duplicate rows) and from two captures
 *  overlapping. */
export function mergeKeys(existing: Iterable<string>, added: Iterable<string>): string[] {
  const set = new Set<string>(existing);
  for (const k of added) { const t = String(k ?? "").trim(); if (t) set.add(t); }
  return [...set];
}
